import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowsClockwise, Trash, Spinner, Globe, EnvelopeSimple, PaperPlaneTilt, Plus } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");

export default function InventorySyncSettings() {
  const [sources, setSources] = useState(null);
  const [running, setRunning] = useState("");
  const [company, setCompany] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newRunNow, setNewRunNow] = useState(true);
  const [adding, setAdding] = useState(false);

  const addSource = async () => {
    setAdding(true);
    try {
      const { data } = await api.post("/import-sources", { url: newUrl.trim(), enabled: true, import_photos: true, auto_mark_sold: true, run_now: newRunNow });
      setSources((s) => [data, ...(s || [])]); setNewUrl("");
      toast.success(newRunNow ? "Link saved — first import is running in the background, check back in a minute" : "Link saved — it will sync tonight at 3:00 AM");
      if (newRunNow) setTimeout(load, 45000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save link"); }
    setAdding(false);
  };

  const load = () => Promise.all([api.get("/import-sources"), api.get("/company")]).then(([s, c]) => { setSources(s.data); setCompany(c.data); }).catch(() => setSources([]));
  useEffect(() => { load(); }, []);

  const saveEmailPrefs = async (patchBody) => {
    const next = { ...company, ...patchBody };
    setCompany(next);
    try { await api.put("/company", { sync_summary_email: next.sync_summary_email === true, sync_summary_recipients: next.sync_summary_recipients || "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
  };
  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/import-sources/send-summary-test", {}, { timeout: 300000 });
      toast.success(data.sent ? `Summary sent to ${data.sent} recipient${data.sent > 1 ? "s" : ""} (${data.new} new, ${data.price} price changes, ${data.sold} sold)` : "No recipients — add an email below or enable the summary");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send"); }
    setSendingTest(false);
  };

  const patch = async (id, body) => {
    try { const { data } = await api.put(`/import-sources/${id}`, body); setSources((s) => s.map((x) => (x.id === id ? data : x))); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this sync source? Existing vehicles stay in inventory.")) return;
    await api.delete(`/import-sources/${id}`); setSources((s) => s.filter((x) => x.id !== id));
  };
  const runNow = async (id) => {
    setRunning(id);
    try {
      const { data } = await api.post(`/import-sources/${id}/run`, {}, { timeout: 300000 });
      toast.success(`Synced: ${data.created} added, ${data.updated} updated, ${data.photos} photos, ${data.sold} marked sold`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    setRunning("");
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="inventory-sync-settings">
      <div>
        <h3 className="font-display font-bold text-lg">Nightly inventory sync</h3>
        <p className="text-sm text-zinc-500 mt-1">Every night at 3:00 AM Central, Orbit re-imports each saved link: mileage is refreshed, new arrivals are added, and vehicles that disappeared from the site are marked Sold. Add a link below or via Inventory → Import from Link / PDF.</p>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="sync-summary-settings">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-semibold text-sm flex items-center gap-2"><EnvelopeSimple size={16} weight="bold" className="text-emerald-600" />Morning summary email</p>
            <p className="text-xs text-zinc-500 mt-0.5">Optional. When checked, admins and managers get an email after each nightly sync listing new arrivals, price changes and sold-outs.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={company?.sync_summary_email === true} onCheckedChange={(v) => saveEmailPrefs({ sync_summary_email: !!v })} data-testid="sync-summary-toggle" />Email me a morning summary after each nightly sync</label>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={company?.sync_summary_recipients || ""} onChange={(e) => setCompany((c) => ({ ...c, sync_summary_recipients: e.target.value }))} onBlur={() => saveEmailPrefs({})} placeholder="Extra recipients, comma-separated (optional)" className="h-10 flex-1" data-testid="sync-summary-recipients" />
          <Button variant="outline" onClick={sendTest} disabled={sendingTest} className="h-10 border-zinc-300 gap-1.5" data-testid="sync-summary-test">{sendingTest ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}Send me a summary now</Button>
        </div>
      </div>
      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3" data-testid="sync-add-source">
        <p className="font-semibold text-sm flex items-center gap-2"><Plus size={16} weight="bold" className="text-emerald-600" />Add a website link</p>
        <p className="text-xs text-zinc-500">Paste your dealer inventory page (one store or the whole group). Orbit will import every vehicle, assign lots, keep each vehicle linked to its own page, and re-sync nightly.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newUrl.trim() && addSource()} placeholder="https://www.yourdealership.com/inventory/used" className="h-10 flex-1" data-testid="sync-add-url" />
          <Button onClick={addSource} disabled={adding || !newUrl.trim()} className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-1.5" data-testid="sync-add-btn">{adding ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}Add link</Button>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={newRunNow} onCheckedChange={(v) => setNewRunNow(!!v)} data-testid="sync-add-run-now" />Import now (otherwise waits for tonight's sync)</label>
      </div>
      {sources === null ? <Spinner size={22} className="animate-spin text-emerald-600" />
        : sources.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center border border-dashed rounded-lg" data-testid="sync-empty">No saved links yet. Add one above or import from a website link once and it will appear here.</p>
        : sources.map((s) => (
          <div key={s.id} className="rounded-lg border border-zinc-200 p-4 space-y-3" data-testid={`sync-source-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-semibold text-sm flex items-center gap-2 break-all"><Globe size={16} className="text-emerald-600 shrink-0" />{s.url}</p>
                <p className="text-xs text-zinc-500 mt-1">Last run: {fmt(s.last_run_at)}{s.last_result ? ` — ${s.last_result.created} added, ${s.last_result.updated} updated, ${s.last_result.sold} sold` : ""}</p>
                {s.last_error && <p className="text-xs text-red-600 mt-1" data-testid={`sync-error-${s.id}`}>Last error: {s.last_error}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="border-zinc-300 gap-1.5" disabled={running === s.id} onClick={() => runNow(s.id)} data-testid={`sync-run-${s.id}`}>
                  {running === s.id ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}Run now
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(s.id)} data-testid={`sync-delete-${s.id}`}><Trash size={15} /></Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-5 text-sm">
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={s.enabled} onCheckedChange={(v) => patch(s.id, { enabled: !!v })} data-testid={`sync-enabled-${s.id}`} />Sync nightly</label>
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={s.import_photos} onCheckedChange={(v) => patch(s.id, { import_photos: !!v })} data-testid={`sync-photos-${s.id}`} />Import photos</label>
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={s.auto_mark_sold} onCheckedChange={(v) => patch(s.id, { auto_mark_sold: !!v })} data-testid={`sync-sold-${s.id}`} />Mark missing vehicles Sold</label>
            </div>
          </div>
        ))}
    </div>
  );
}
